<!-- TimeseriesGraph.vue -->
<!-- Full-size graph overlay for timeseries query results -->
<template>
  <div class="graph-overlay" v-if="visible" @click.self="$emit('close')">
    <div class="graph-modal">

      <!-- ── Header ──────────────────────────────────────── -->
      <div class="graph-header">
        <div class="graph-header-left">
          <i class="bi bi-graph-up-arrow"></i>
          <span>Graph View</span>
          <span class="data-badge">{{ queryResults.length }} points</span>
        </div>
        <div class="graph-header-right">
          <div class="chart-type-toggle">
            <button
              v-for="t in chartTypes" :key="t.value"
              class="chart-type-btn"
              :class="{ active: chartType === t.value }"
              @click="chartType = t.value"
              :title="t.label"
            >
              <i :class="t.icon"></i> {{ t.label }}
            </button>
          </div>
          <button class="close-btn" @click="$emit('close')" title="Close graph">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>

      <!-- ── Axis Controls ────────────────────────────────── -->
      <div class="graph-controls">
        <!-- X Axis -->
        <div class="axis-block">
          <label class="axis-label x-label">X Axis</label>
          <select v-model="xAxis" class="axis-select">
            <option value="_timestamp">Timestamp</option>
            <option v-for="key in numericKeys" :key="key" :value="key">{{ formatSeriesLabel(key) }}</option>
          </select>
        </div>

        <div class="axis-divider"></div>

        <!-- Y Axis series -->
        <div class="axis-block y-block">
          <label class="axis-label y-label">Y Axis &mdash; select series</label>
          <div v-if="numericKeys.length === 0" class="no-numeric-hint">
            No numeric (int/float) keys available in results yet.
          </div>
          <div v-else class="y-series-list">
            <label
              v-for="(key, idx) in numericKeys"
              :key="key"
              class="y-series-chip"
              :class="{ selected: ySeries.includes(key) }"
              :style="ySeries.includes(key) ? { borderColor: palette[idx % palette.length], background: palette[idx % palette.length] + '18' } : {}"
            >
              <input type="checkbox" :value="key" v-model="ySeries" />
              <span class="series-dot" :style="{ background: palette[idx % palette.length] }"></span>
              <span class="series-name">{{ formatSeriesLabel(key) }}</span>
            </label>
          </div>
        </div>
      </div>

      <!-- ── Chart Canvas ─────────────────────────────────── -->
      <div class="graph-body" ref="chartContainer">
        <div v-if="ySeries.length === 0" class="graph-empty">
          <i class="bi bi-bar-chart-line"></i>
          <p>Select at least one Y axis series above to build the graph</p>
        </div>
        <div v-else-if="queryResults.length === 0" class="graph-empty">
          <i class="bi bi-bar-chart-line"></i>
          <p>Run a query in the Browse tab first to load data</p>
        </div>
        <canvas v-show="ySeries.length > 0 && queryResults.length > 0" ref="chartCanvas"></canvas>
      </div>

      <!-- ── Footer ──────────────────────────────────────── -->
      <div class="graph-footer">
        <span v-if="ySeries.length > 0 && queryResults.length > 0">
          {{ ySeries.length }} series &times; {{ queryResults.length }} rows = {{ ySeries.length * queryResults.length }} data points
        </span>
        <span v-else class="muted">No data displayed</span>
      </div>

    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  ScatterController,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';

Chart.register(
  LineController,
  ScatterController,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const PALETTE = [
  '#18392B', '#2196F3', '#FF9800', '#E91E63',
  '#9C27B0', '#00BCD4', '#FF5722', '#607D8B',
  '#8BC34A', '#FFC107', '#795548', '#3F51B5',
];

// Smart timestamp formatter
function fmtTs(ms) {
  if (!ms && ms !== 0) return '';
  return new Date(ms).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default {
  name: 'TimeseriesGraph',
  props: {
    visible:      { type: Boolean,  default: false },
    queryResults: { type: Array,    default: () => [] },
    numericKeys:  { type: Array,    default: () => [] },
    seriesMetaMap:{ type: Object,   default: () => ({}) },
    getRowValues: { type: Function, required: true },
  },
  emits: ['close'],

  created() {
    // Store chart instance non-reactively to avoid Vue observer overhead
    this.chartInst = null;
  },

  data() {
    return {
      xAxis:     '_timestamp',
      ySeries:   [],
      chartType: 'line',
      palette:   PALETTE,
      chartTypes: [
        { value: 'line',    label: 'Line',    icon: 'bi bi-graph-up' },
        { value: 'scatter', label: 'Scatter', icon: 'bi bi-grid-3x3-gap' },
      ],
    };
  },

  watch: {
    visible(val) {
      if (val) {
        // Auto-select first key on first open
        if (this.ySeries.length === 0 && this.numericKeys.length > 0) {
          this.ySeries = this.numericKeys.slice(0, Math.min(3, this.numericKeys.length));
        }
        this.$nextTick(() => this.rebuildChart());
      } else {
        this.destroyChart();
      }
    },
    chartType()    { this.$nextTick(() => this.rebuildChart()); },
    xAxis()        { this.$nextTick(() => this.rebuildChart()); },
    ySeries()      { this.$nextTick(() => this.rebuildChart()); },
    queryResults() { if (this.visible) this.$nextTick(() => this.rebuildChart()); },
    numericKeys(keys) {
      if (this.ySeries.length === 0 && keys.length > 0) {
        this.ySeries = keys.slice(0, Math.min(3, keys.length));
      }
    },
  },

  beforeUnmount() {
    this.destroyChart();
  },

  methods: {
    getSeriesMeta(key) {
      const normalizedKey = String(key || '').trim();
      if (!normalizedKey) return {};

      const direct = this.seriesMetaMap?.[normalizedKey];
      if (direct) return direct;

      const target = normalizedKey.toLowerCase();
      for (const [seriesName, meta] of Object.entries(this.seriesMetaMap || {})) {
        if (String(seriesName).trim().toLowerCase() === target) {
          return meta || {};
        }
      }
      return {};
    },

    formatSeriesLabel(key) {
      const meta = this.getSeriesMeta(key);
      const unit = (meta.unit || '').trim();
      if (unit) return `${key} (${unit})`;
      return key;
    },

    destroyChart() {
      if (this.chartInst) { this.chartInst.destroy(); this.chartInst = null; }
    },

    rebuildChart() {
      this.destroyChart();
      const canvas = this.$refs.chartCanvas;
      if (!canvas || this.ySeries.length === 0 || this.queryResults.length === 0) return;

      const isTimeX = this.xAxis === '_timestamp';

      const datasets = this.ySeries.map((key, idx) => {
        const color = this.palette[idx % this.palette.length];
        const points = [];
        for (const pt of this.queryResults) {
          const rowVals = this.getRowValues(pt);
          const x = isTimeX
            ? (pt.ts ? new Date(pt.ts).getTime() : null)
            : Number(rowVals?.[this.xAxis]);
          const y = Number(rowVals?.[key]);
          if (x == null || isNaN(x) || isNaN(y)) continue;
          points.push({ x, y });
        }
        const isScatter = this.chartType === 'scatter';
        const useDots = !isScatter && points.length > 500;
        return {
          label: this.formatSeriesLabel(key),
          rawKey: key,
          data: points,
          borderColor: color,
          backgroundColor: color + (isScatter ? '99' : '28'),
          pointRadius: isScatter ? (points.length > 500 ? 4 : 6) : (useDots ? 1.5 : 3),
          pointHoverRadius: isScatter ? 9 : 6,
          pointBorderWidth: isScatter ? 1.5 : 0,
          pointBorderColor: isScatter ? color : undefined,
          borderWidth: isScatter ? 0 : 2,
          fill: false,
          tension: 0.25,
          showLine: !isScatter,
        };
      });

      const xTicks = isTimeX
        ? {
            color: '#666',
            maxTicksLimit: 8,
            callback: (val) => {
              // Smart formatting: if range < 2 days show time, else show date
              const range = this.queryResults.length > 1
                ? (new Date(this.queryResults[this.queryResults.length - 1].ts).getTime() -
                   new Date(this.queryResults[0].ts).getTime())
                : 0;
              const twoDays = 2 * 86400 * 1000;
              return range < twoDays
                ? new Date(val).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                : new Date(val).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            },
          }
        : { color: '#666', maxTicksLimit: 8 };

      this.chartInst = new Chart(canvas, {
        type: this.chartType === 'scatter' ? 'scatter' : 'line',
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 250 },
          interaction: { mode: 'index', intersect: false, axis: 'x' },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 20,
                color: '#333',
                font: { size: 12 },
              },
            },
            tooltip: {
              backgroundColor: 'rgba(18,40,28,0.93)',
              padding: 12,
              cornerRadius: 7,
              titleColor: '#cdfae1',
              bodyColor: '#e8f5e9',
              bodySpacing: 5,
              usePointStyle: true,
              callbacks: {
                title: (items) => {
                  const raw = items[0]?.raw;
                  if (isTimeX && raw?.x != null) return fmtTs(raw.x);
                  return String(raw?.x ?? '');
                },
                label: (item) => {
                  const y = item.raw?.y;
                  const seriesName = item.dataset?.label || this.formatSeriesLabel(item.dataset?.rawKey || '');
                  return ` ${seriesName}: ${y != null ? y.toLocaleString(undefined, { maximumFractionDigits: 6 }) : '—'}`;
                },
              },
            },
          },
          scales: {
            x: {
              type: 'linear',
              title: {
                display: true,
                text: isTimeX ? 'Timestamp' : this.formatSeriesLabel(this.xAxis),
                color: '#555',
                font: { size: 11 },
              },
              ticks: xTicks,
              grid: { color: '#ebebea' },
            },
            y: {
              title: {
                display: this.ySeries.length === 1,
                text: this.ySeries[0] ? this.formatSeriesLabel(this.ySeries[0]) : '',
                color: '#555',
                font: { size: 11 },
              },
              ticks: { color: '#666', maxTicksLimit: 8 },
              grid: { color: '#ebebea' },
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
/* ── Overlay ──────────────────────────────────────── */
.graph-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
}

.graph-modal {
  width: 90vw;
  max-width: 90vw;
  height: 90vh;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 48px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ──────────────────────────────────────── */
.graph-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 20px;
  border-bottom: 1px solid #deded6;
  background: #f6f6f2;
  flex-shrink: 0;
}

.graph-header-left {
  display: flex;
  align-items: center;
  gap: 9px;
  font-weight: 700;
  font-size: 1rem;
  color: #1a3a2a;
}
.graph-header-left i { font-size: 1.1rem; color: #18392B; }
.data-badge {
  background: #e8f5ec;
  color: #18392B;
  border-radius: 12px;
  padding: 2px 9px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid #c8e8d0;
}

.graph-header-right { display: flex; align-items: center; gap: 12px; }

.chart-type-toggle {
  display: flex;
  border: 1px solid #d0d0c8;
  border-radius: 6px;
  overflow: hidden;
}
.chart-type-btn {
  background: #f0f0eb;
  border: none;
  padding: 6px 14px;
  font-size: 0.82rem;
  cursor: pointer;
  color: #555;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.15s, color 0.15s;
}
.chart-type-btn:not(:last-child) { border-right: 1px solid #d0d0c8; }
.chart-type-btn.active { background: #18392B; color: #fff; }
.chart-type-btn:hover:not(.active) { background: #e2e2dc; }

.close-btn {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: #888;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  line-height: 1;
}
.close-btn:hover { background: #e8e8e0; color: #333; }

/* ── Controls ────────────────────────────────────── */
.graph-controls {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 14px 20px;
  border-bottom: 1px solid #e8e8e2;
  background: #fafaf7;
  flex-shrink: 0;
}

.axis-block { display: flex; flex-direction: column; gap: 8px; }
.y-block { flex: 1; min-width: 0; }

.axis-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 700;
  color: #aaa;
}
.x-label { color: #2196F3; }
.y-label { color: #4CAF50; }

.axis-select {
  border: 1px solid #d0d0c8;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 0.85rem;
  color: #333;
  background: #fff;
  min-height: 32px;
  min-width: 170px;
}
.axis-select:focus { outline: none; border-color: #2196F3; }

.axis-divider {
  width: 1px;
  background: #e0e0d8;
  align-self: stretch;
  margin: 0 22px;
  flex-shrink: 0;
}

.y-series-list { display: flex; flex-wrap: wrap; gap: 8px; }

.y-series-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 4px 8px;
  border: 1.5px solid #d8d8d0;
  border-radius: 20px;
  font-size: 0.82rem;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
  user-select: none;
}
.y-series-chip:hover { border-color: #bbb; background: #f5f5f0; }
.y-series-chip.selected { font-weight: 600; }
.y-series-chip input[type=checkbox] { margin: 0; cursor: pointer; }

.series-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}

.no-numeric-hint { font-size: 0.82rem; color: #bbb; font-style: italic; margin-top: 4px; }

/* ── Chart body ─────────────────────────────────── */
.graph-body {
  flex: 1;
  min-height: 0;
  padding: 16px 20px;
  position: relative;
  overflow: hidden;
}
.graph-body canvas {
  width: 100% !important;
  height: 100% !important;
}

.graph-empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ccc;
  gap: 14px;
}
.graph-empty i { font-size: 3.2rem; }
.graph-empty p { font-size: 0.9rem; color: #aaa; }

/* ── Footer ─────────────────────────────────────── */
.graph-footer {
  padding: 7px 20px;
  border-top: 1px solid #e8e8e0;
  font-size: 0.76rem;
  background: #fafaf7;
  flex-shrink: 0;
  color: #aaa;
}
.muted { color: #ccc; }
</style>
